import { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import TwilioVoiceWebRTC from './webrtc/TwilioVoiceWebRTC';

const Call = ({ apiUrl }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [address, setAddress] = useState('');
  const [hostId, setHostId] = useState('');
  const [callStarted, setCallStarted] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Extract contact id and address from location state
    const id = location.state?.contactId || '';
    const addr = location.state?.address || '';
    setHostId(id);
    setAddress(addr);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStartCall = () => {
    if (!hostId) {
      setError('No contact selected');
      return;
    }
    setCallStarted(true);
  };

  const handleCallEnd = () => {
    // Navigate back to directory after call ends
    navigate(`/directory?${searchParams.toString()}`);
  };

  const handleBack = () => {
    navigate(`/directory?${searchParams.toString()}`);
  };

  // If call started, show WebRTC component
  if (callStarted) {
    return (
      <TwilioVoiceWebRTC
        apiUrl={apiUrl}
        hostId={hostId}
        address={address}
        queryString={searchParams.toString()}
        onCallEnd={handleCallEnd}
      />
    );
  }

  // Pre-call UI
  return (
    <Wrapper>
      <Container>
        <TopBarDiv>
          <BackButton onClick={handleBack} style={{color: "white"}}>⬅</BackButton>
          <Header>{address || 'Unknown Address'}</Header>
        </TopBarDiv>

        {error && <ErrorMsg>{error}</ErrorMsg>}

        <InfoTextLarge>
          You are about to start a call with {address || 'this contact'}.
        </InfoTextLarge>
        <InfoText style={{ fontSize: '14px', marginTop: '8px', color: '#999' }}>
          This call will connect directly through your browser for a secure session.
        </InfoText>

        <StartButton
          onClick={handleStartCall}
          disabled={!hostId}
        >
          {`Call ${address || 'Contact'}`}
        </StartButton>
      </Container>
    </Wrapper>
  );
};

export default Call;

const Wrapper = styled.div`
  width: 100%;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: #000;
`;

const TopBarDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  position: relative;
  margin-bottom: 30px;

  h1 {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    margin: 0;
  }
`;

const Container = styled.div`
  max-width: 600px;
  width: 100%;
  margin: 0 auto;
  padding: 20px;
  color: #fff;
  box-sizing: border-box;
`;

const Header = styled.h1`
  margin: 12px 0 24px 0;
  color: #fff;
  font-size: 22px;
  text-align: center;
`;

const InfoTextLarge = styled.div`
  padding: 12px;
  color: #eee;
  font-size: 20px;
  font-weight: 500;
  margin-top: 16px;
  text-align: center;
  line-height: 1.5;
`;

const InfoText = styled.div`
  padding: 12px;
  color: #ccc;
  font-size: 16px;
  margin-top: 16px;
  text-align: center;
  line-height: 1.5;
`;

const ErrorMsg = styled.div`
  padding: 16px;
  color: #ffb3b3;
  background: rgba(255, 67, 54, 0.15);
  border: 1px solid rgba(255, 67, 54, 0.3);
  border-radius: 8px;
  margin: 12px 0;
  font-size: 15px;
  line-height: 1.4;
  text-align: center;
`;

const StartButton = styled.button`
  width: 100%;
  padding: 18px 24px;
  font-size: 1.3em;
  font-weight: 600;
  background: ${props => props.disabled ? '#666' : '#4caf50'};
  color: #fff;
  border: none;
  border-radius: 40px;
  margin-top: 32px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.3);

  &:hover:not(:disabled) {
    background: #388e3c;
    transform: translateY(-2px);
    box-shadow: 0 6px 8px rgba(0, 0, 0, 0.4);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  }

  &:disabled {
    opacity: 0.7;
  }
`;

const BackButton = styled.button`
  padding: 10px 20px 10px 5px;
  font-size: 16px;
  background-color: black;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;

  &:hover {
    background-color: #B70100;
  }

  &:active {
    background-color: #821010ff;
  }
`;
